import { createClient } from '@supabase/supabase-js'

// Supabase client initialization
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || ''

export const supabase = createClient(supabaseUrl, supabaseKey)

// ===== AUTH FUNCTIONS =====

export const signUp = async (email: string, password: string, name: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: name
      }
    }
  })

  if (error) throw error
  return data
}

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })

  if (error) throw error
  return data
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw error
  return data.user
}

// ===== CHART FUNCTIONS =====

export interface ChartData {
  id?: string
  book: string
  chapter: number
  classification: string
  unity: string
  problem: string
  key_terms: Array<{
    term: string
    definition: string
    verses: number[]
  }>
  paragraphs: Array<{
    id: string
    number: number
    title: string
    startVerse: number
    endVerse: number
    topic: string
    summary: string
    keyPoints: string[]
    interpretiveNotes: string
  }>
  interpretive_notes?: string
  title?: string
  is_public?: boolean
}

export const createChart = async (chartData: ChartData) => {
  const { data: { user }, error: userError } = await supabase.auth.getUser()
  if (userError) throw userError

  // First, ensure the chapter exists
  const { data: existingChapter } = await supabase
    .from('chapters')
    .select('id')
    .eq('book', chartData.book)
    .eq('chapter_num', chartData.chapter)
    .single()

  let chapterId = existingChapter?.id

  if (!chapterId) {
    const { data: newChapter, error: chapterError } = await supabase
      .from('chapters')
      .insert({
        book: chartData.book,
        chapter_num: chartData.chapter,
        verses_count: 0
      })
      .select('id')
      .single()

    if (chapterError) throw chapterError
    chapterId = newChapter.id
  }

  // Create the chart
  const { data, error } = await supabase
    .from('charts')
    .insert({
      user_id: user?.id,
      chapter_id: chapterId,
      classification: chartData.classification,
      unity: chartData.unity,
      problem: chartData.problem,
      key_terms: chartData.key_terms,
      paragraphs: chartData.paragraphs,
      interpretive_notes: chartData.interpretive_notes || '',
      title: chartData.title || `${chartData.book} ${chartData.chapter}`,
      is_public: chartData.is_public || false
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export const getCharts = async () => {
  const { data, error } = await supabase
    .from('charts')
    .select(`
      id,
      title,
      is_public,
      created_at,
      updated_at,
      chapters!inner(book, chapter_num)
    `)
    .order('updated_at', { ascending: false })

  if (error) throw error
  return data
}

export const getChart = async (chartId: string) => {
  const { data, error } = await supabase
    .from('charts')
    .select(`
      *,
      chapters!inner(book, chapter_num)
    `)
    .eq('id', chartId)
    .single()

  if (error) throw error
  return data
}

export const updateChart = async (chartId: string, updates: Partial<ChartData>) => {
  const { data, error } = await supabase
    .from('charts')
    .update({
      classification: updates.classification,
      unity: updates.unity,
      problem: updates.problem,
      key_terms: updates.key_terms,
      paragraphs: updates.paragraphs,
      interpretive_notes: updates.interpretive_notes,
      title: updates.title,
      is_public: updates.is_public,
      updated_at: new Date().toISOString()
    })
    .eq('id', chartId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const deleteChart = async (chartId: string) => {
  const { error } = await supabase
    .from('charts')
    .delete()
    .eq('id', chartId)

  if (error) throw error
}

// ===== CHAPTER (BIBLE) FUNCTIONS =====

export const fetchChapter = async (book: string, chapter: number) => {
  // First check if we have it cached
  const { data: cached } = await supabase
    .from('chapters')
    .select('*')
    .eq('book', book)
    .eq('chapter_num', chapter)
    .single()

  if (cached) {
    return cached
  }

  // Call the Edge Function to fetch from ESV API
  const { data, error } = await supabase.functions.invoke('fetch-chapter', {
    body: { book, chapter }
  })

  if (error) throw error

  // Cache it
  const { data: cachedData, error: cacheError } = await supabase
    .from('chapters')
    .insert({
      book,
      chapter_num: chapter,
      verses_count: data.passages?.[0]?.match(/\[(\d+)\]/g)?.length || 0,
      text: data
    })
    .select()
    .single()

  if (cacheError) throw cacheError
  return cachedData
}

// ===== SHARING FUNCTIONS =====

export const shareChart = async (
  chartId: string,
  email: string,
  permissionLevel: 'view' | 'edit' = 'view'
) => {
  // Get the user ID from email
  const { data: users, error: userError } = await supabase
    .rpc('get_user_id_by_email', { user_email: email })

  if (userError) throw userError
  if (!users || users.length === 0) throw new Error('User not found')

  const targetUserId = users[0].id

  // Create share record
  const { data, error } = await supabase
    .from('shared_charts')
    .insert({
      chart_id: chartId,
      shared_with_user_id: targetUserId,
      permission_level: permissionLevel
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export const getSharedCharts = async () => {
  const { data, error } = await supabase
    .from('shared_charts')
    .select(`
      id,
      permission_level,
      created_at,
      charts!inner(id, title, user_id, chapters!inner(book, chapter_num))
    `)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export const revokeShare = async (shareId: string) => {
  const { error } = await supabase
    .from('shared_charts')
    .delete()
    .eq('id', shareId)

  if (error) throw error
}

// ===== REAL-TIME SUBSCRIPTIONS =====

export const subscribeToChart = (
  chartId: string,
  callback: (payload: any) => void
) => {
  return supabase
    .channel(`charts:${chartId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'charts', filter: `id=eq.${chartId}` },
      (payload) => {
        callback(payload)
      }
    )
    .subscribe()
}

export const unsubscribeFromChart = (subscription: any) => {
  subscription.unsubscribe()
}

// ===== SUBSCRIPTION CHECK =====

export const checkSubscription = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return false

  // is_comped bypasses subscription requirement
  const { data: settings } = await supabase
    .from('user_settings')
    .select('is_comped')
    .eq('user_id', user.id)
    .maybeSingle()

  if (settings?.is_comped) return true

  const { data: ents } = await supabase
    .from('user_entitlements')
    .select('id, expires_at')
    .eq('user_id', user.id)
    .in('product_id', ['subscription_pro', 'subscription_pro_annual'])

  return !!(ents || []).some(e => !e.expires_at || new Date(e.expires_at) > new Date())
}
